import {
  Dialog,
  IconButton,
  Button,
  Stack,
  Box,
  FormHelperText,
  Typography,
  MenuItem,
  Select as MuiSelect
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'

import { TIMEZONES, getDefaultTimezone, formatTimezoneDisplay } from 'utils/timezones'
import { DatePicker, DatePickerAnchorField } from 'components/global/CUK/DatePicker'
import { ActionIcon } from 'components/Modal/ActionModal'
import ScrollPicker from 'packages/ui/ScrollPicker'
import { useSites } from 'store'

import { InputAlarmBulk, useBulk, useDestroy } from './use-actions'
import { toast } from 'components/global/CUK/Toast/Toast'

type AlarmFormProps = {
  room_numbers: string[]
  start_date?: string
  end_date?: string
  alarm_time?: string
  timezone?: string
  onClose: () => void
  onSubmit: () => void
}

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))

const getTomorrow = () => moment().add(1, 'day').format('YYYY-MM-DD')

const parseAlarmTime = (alarmTime?: string) => {
  if (!alarmTime) return { hour: '07', minute: '00' }
  const [hour, minute] = alarmTime.split(':')
  return { hour: hour || '07', minute: minute || '00' }
}


const schema = (t: (key: string) => string) =>
  yup.object().shape({
    site_sf_id: yup.string().required(),
    room_numbers: yup.array().of(yup.string().required()).min(1).required(),
    start_date: yup.string().required(t('ui_elements.required_field')),
    end_date: yup.string().required(t('ui_elements.required_field')),
    alarm_hour: yup.string().required(t('ui_elements.required_field')),
    alarm_minute: yup.string().required(t('ui_elements.required_field')),
    timezone: yup.string().required(t('ui_elements.required_field')),
  })

const FormAlarm = ({ room_numbers, start_date, end_date, alarm_time, timezone, onSubmit, onClose }: AlarmFormProps) => {
  const [{ selected }] = useSites()
  const [bulk, { loading: loadingBulk, error: errorBulk }] = useBulk()
  const [destroy, { loading: loadingDestroy, error: errorDestroy }] = useDestroy()
  const { t } = useTranslation()
  const { hour: defaultHour, minute: defaultMinute } = parseAlarmTime(alarm_time)
  const methods = useForm<InputAlarmBulk>({
    defaultValues: {
      site_sf_id: selected[0].sf_id || '',
      room_numbers,
      start_date: start_date || getTomorrow(),
      end_date: end_date || getTomorrow(),
      alarm_hour: defaultHour,
      alarm_minute: defaultMinute,
      timezone: timezone || getDefaultTimezone().label
    },
    resolver: yupResolver(schema(t)),
    mode: 'onSubmit'
  })

  const handleSubmit = async (values: InputAlarmBulk) => {
    const { error } = await bulk(values)
    if (error) {
      toast.error(t('voip.sip.alarm.form.error'))
    } else {
      onSubmit()
      toast.success(t('voip.sip.alarm.form.success', { count: values.room_numbers.length }))
    }
  }

  const handleDestroy = async () => {
    const { error } = await destroy({
      site_sf_id: selected[0].sf_id || '',
      room_numbers
    })
    if (error) {
      toast.error(t('voip.sip.alarm.form.error'))
    } else {
      onSubmit()
      toast.success(t('voip.sip.alarm.form.destroy_success', { count: room_numbers.length }))
    }
  }

  return (
    <FormProvider {...methods}>
      {Boolean(errorBulk || errorDestroy) && (
        <Typography sx={{ fontSize: 12, fontWeight: 500, color: 'error.main' }}>
          {t(`errors.sip.${errorBulk || errorDestroy}`)}
        </Typography>
      )}
      <Stack gap={2} sx={{ mt: 2 }}>
        <Controller
          control={methods.control}
          name="start_date"
          render={({ field: { value }, fieldState }) => (
            <Box>
              <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
                {t('voip.sip.alarm.form.dates')}
              </Typography>
              <DatePicker
                placement="left-start"
                name="start_date"
                defaultStartDate={value ? new Date(value) : undefined}
                defaultEndDate={methods.watch('end_date') ? new Date(methods.watch('end_date')!) : undefined}
                blockPastDates={true}
                editMode={false}
                field={
                  <DatePickerAnchorField
                    isdate={true}
                    locale="fr"
                    startDate={value ? new Date(value) : null}
                    endDate={methods.watch('end_date') ? new Date(methods.watch('end_date')!) : null}
                    placeholder={t('event_ssids.form.dates_placeholder')}
                    onReset={() => {
                      methods.setValue('start_date', start_date)
                      methods.setValue('end_date', end_date)
                    }}
                    inputSize="large"
                    hasError={Boolean(fieldState.error)}
                    sx={{
                      width: '100%'
                    }}
                  />
                }
                onSubmit={(start, end) => {
                  methods.setValue('start_date', start)
                  methods.setValue('end_date', end)
                }}
              />
              {fieldState.error && <FormHelperText error>{fieldState.error.message}</FormHelperText>}
            </Box>
          )}
        />

        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
            {t('voip.sip.alarm.form.alarm_time')}
          </Typography>
          <Stack direction="row" gap={1} alignItems="flex-start">
            <Controller
              control={methods.control}
              name="alarm_hour"
              render={({ field: { value, onChange } }) => (
                <ScrollPicker
                  items={HOURS}
                  value={value ?? defaultHour}
                  onChange={onChange}
                  label={t('voip.sip.alarm.form.hour')}
                />
              )}
            />
            <Stack justifyContent="center" sx={{ pt: 3 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 600, color: 'text.secondary' }}>:</Typography>
            </Stack>
            <Controller
              control={methods.control}
              name="alarm_minute"
              render={({ field: { value, onChange } }) => (
                <ScrollPicker
                  items={MINUTES}
                  value={value ?? defaultMinute}
                  onChange={onChange}
                  label={t('voip.sip.alarm.form.minute')}
                />
              )}
            />
          </Stack>
        </Box>
        <Box>
          <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#333', mb: 1 }}>
            {t('voip.sip.alarm.form.timezone')}
          </Typography>
          <Controller
            control={methods.control}
            name="timezone"
            render={({ field: { value, onChange }, fieldState }) => (
              <MuiSelect
                value={value}
                onChange={e => onChange(e.target.value)}
                error={Boolean(fieldState.error)}
                displayEmpty
                size="small"
                sx={{
                  height: '40px',
                  borderRadius: '8px',
                  fontSize: 12,
                  width: '100%',
                  backgroundColor: 'white',
                  '& .MuiSelect-select': {
                    fontSize: 12,
                    display: 'flex',
                    alignItems: 'center',
                    paddingLeft: '12px',
                    height: '40px'
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: fieldState.error ? 'error.main' : '#EFEFEF',
                    borderRadius: '8px'
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: fieldState.error ? 'error.main' : '#EFEFEF'
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: fieldState.error ? 'error.main' : '#2570EB'
                  }
                }}
                IconComponent={props => (
                  <i
                    {...props}
                    className="fa-regular fa-chevron-down"
                    style={{
                      fontSize: 12,
                      color: '#464646',
                      marginRight: 12,
                      pointerEvents: 'none'
                    }}
                  />
                )}
                startAdornment={
                  <i
                    className="fa-regular fa-globe"
                    style={{
                      fontSize: 12,
                      color: '#656565',
                      marginRight: 8
                    }}
                  />
                }>
                {TIMEZONES.map(tz => (
                  <MenuItem key={tz.label} value={tz.label} sx={{ fontSize: 12 }}>
                    {formatTimezoneDisplay(tz, t)}
                  </MenuItem>
                ))}
              </MuiSelect>
            )}
          />
        </Box>
        <Stack mt={2} direction="row" justifyContent={!(room_numbers.length === 1 && Boolean(start_date)) ? 'flex-end' : 'space-between'}>
          {
            room_numbers.length === 1 && Boolean(start_date) && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                sx={{
                  borderColor: 'error.main',
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.main',
                    color: 'white'
                  }
                }}
                onClick={handleDestroy}
                disabled={loadingBulk || loadingDestroy}>
                <i className={`fa-solid fa-${!loadingDestroy ? 'trash' : 'spinner fa-spin'}`} style={{ marginRight: 8 }} />
                {loadingDestroy ? t('ui_elements.in_progress') : t('ui_elements.remove')}
              </Button>
            )
          }
          <Stack direction="row" gap={1}>
            <Button size="small" variant="outlined" color="primary" onClick={onClose} disabled={loadingBulk}>
              {t('ui_elements.cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={methods.handleSubmit(handleSubmit)}
              disabled={loadingBulk}>
              {loadingBulk && (
                <i className={'fa-solid fa-spinner fa-spin'} style={{ marginRight: 8 }} />
              )}
              {loadingBulk ? t('ui_elements.in_progress') : t('ui_elements.save')}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </FormProvider>
  )
}

const ModalAlarm = ({ room_numbers, start_date, end_date, alarm_time, timezone, onSubmit, onClose }: AlarmFormProps) => {
  const { t } = useTranslation()
  const isCreate = !start_date && !end_date && !alarm_time

  return (
    <Dialog
      style={{ backgroundColor: 'transparent' }}
      maxWidth='md'
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start',
          paddingTop: '10vh',
        },
      }}
      PaperProps={{
        style: {
          width: '480px',
          borderRadius: '24px',
          padding: '20px',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh'
        }
      }}
      open
    >
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 32,
          zIndex: 1,
        }}
      >
        <CloseIcon />
      </IconButton>
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', margin: 2, marginTop: 1 }}>
        <Stack direction="row" alignItems="center" gap={1.5}>
          <ActionIcon>
            <i className={`fa-solid fa-${isCreate ? 'alarm-plus' : 'alarm-clock'}`} />
          </ActionIcon>
          <Typography color='primary.main' fontSize={20} fontWeight={300}>
            {
            room_numbers.length > 1
              ? t('voip.sip.alarm.form.title_update_multiple')
              : isCreate
                ? t('voip.sip.alarm.form.title_create')
                : t('voip.sip.alarm.form.title_update')
            }
          </Typography>
        </Stack>
        <FormAlarm
          room_numbers={room_numbers}
          start_date={start_date}
          end_date={end_date}
          alarm_time={alarm_time}
          onSubmit={onSubmit}
          onClose={onClose}
          timezone={timezone}
        />
      </Stack>
    </Dialog>
  )
}

export default ModalAlarm